/**
 * Screen 1a — log workstation.
 *
 * Project tree on the left, composite log in the middle, interpretation
 * parameters on the right. "Compute interval" runs the deterministic workflow
 * on the server and republishes the INTERPRETASI curve set, which this screen
 * then reloads.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { api, unpage, type Page } from "../api";
import { canCompute, canWrite } from "../auth";
import { Icon } from "../components/icons";
import {
  BoxZoomOverlay,
  DEPTH_GUTTER_W,
  DepthGutter,
  LogTrack,
  TrackHeaders,
  sampleAt,
  toTrackCurve,
  tracksMinWidth,
  type TrackCurve,
  type TrackSpec,
} from "../components/LogTracks";
import {
  MarkerDialog,
  MarkerHandles,
  markerActions,
  pickedDepth,
  type MarkerDraft,
} from "../components/MarkerTools";
import {
  RailCollapseButton,
  RailStrip,
  RailTabBar,
  type RailTabItem,
} from "../components/RailTabs";
import { Resizer } from "../components/Resizer";
import { CutoffFields } from "../components/CutoffTool";
import { DeleteDialog } from "../components/DeleteDialog";
import { ExportDialog, type ExportCurve } from "../components/ExportDialog";
import { PermFields } from "../components/PermTool";
import { PhieFields } from "../components/PhieTool";
import { PhitFields } from "../components/PhitTool";
import { SwFields } from "../components/SwTool";
import { VshFields } from "../components/VshTool";
import CurveInspector from "../components/CurveInspector";
import { kinOptions } from "../lib/curvekin";
import { classBlocks } from "../lib/lrlc";
import { MiniButton, TrackEditor } from "../components/TrackEditor";
import {
  Button,
  ContextMenu,
  Dropdown,
  Empty,
  KeyValue,
  Loading,
  Notice,
  Panel,
  Slider,
  StatusDot,
  Toggle,
  useToast,
} from "../components/ui";
import { useAsync, useStored } from "../hooks";
import { logColorsUrl, type LogColorPayload } from "../lib/logcolors";
import {
  axisSpan,
  orientRange,
  DEPTH_HINT,
  DEPTH_LABEL,
  DEPTH_ORDER,
} from "../lib/depthaxis";
import {
  DEPTH_SCALE_PRESETS,
  depthTickCount,
  stepScale,
} from "../lib/depthscale";
import { useDepthSheet } from "../lib/depthsheet";
import {
  addTrack,
  defaultCurveColor,
  describedLithology,
  lithologyFromVsh,
  defaultTracks,
  formatReading,
  makeTrack,
  normalizeTracks,
  updateCurve,
  moveTrackTo,
  updateTrack,
  type CurveConfig,
  type SpectrumConfig,
  type TrackConfig,
} from "../lib/tracklayout";
import { unitsFor } from "../lib/units";
import type {
  AnalysisRun,
  CoreDescription,
  CurveData,
  DepthReference,
  LogsPayload,
  ParameterSet,
  ParameterValues,
  Project,
  Well,
} from "../types";

/** Tab rel kanan; kuncinya `RAIL_TABS`. */
type RailTab = "parameters" | "tracks" | "curves" | "results";

/**
 * Tab rel kanan beserta namanya, dalam urutan yang ditampilkan.
 *
 * Satu daftar, sebab sejak rel ini bisa dilipat ia dicetak dua kali: mendatar
 * di kepala rel yang terbuka, dan menurun di pita rel yang terlipat. Dua daftar
 * yang disalin adalah tempat tab kelima nanti terlupakan di salah satunya.
 */
const RAIL_TABS: RailTabItem<RailTab>[] = [
  { key: "parameters", label: "Parameter" },
  { key: "tracks", label: "Track" },
  { key: "curves", label: "Curves" },
  { key: "results", label: "Results" },
];

/**
 * Rel kiri, satu-satunya isinya, sebagai daftar tab yang sama bentuknya.
 *
 * Ia memang hanya satu nama. Tetapi pita rel kiri yang terlipat dicetak oleh
 * komponen yang sama dengan pita rel kanan, dan yang membedakan sebuah pita
 * dari sebuah tepi bukanlah banyaknya nama melainkan bahwa namanya terbaca —
 * "Project", kata yang sama dengan yang tertulis di kepala relnya ketika ia
 * terbuka.
 */
const TREE_TABS: RailTabItem<"project">[] = [{ key: "project", label: "Project" }];

export default function Workstation({
  project,
  well,
  wells = [],
  dataVersion = 0,
  onDataChanged,
}: {
  project: Project;
  well: Well;
  /** Sumur lain di proyek ini — sasaran "pasang di semua sumur". */
  wells?: Well[];
  /** Changes when the project's curves have been edited elsewhere. */
  dataVersion?: number;
  onDataChanged: () => void;
}) {
  const toast = useToast();
  const mayCompute = canCompute(project.my_role);
  const mayWrite = canWrite(project.my_role);
  const units = unitsFor(project.unit_system);

  /**
   * Penyuntingan marker di lembar log.
   *
   * `dragged` menahan kedalaman marker yang sedang diseret sebelum server
   * tahu. Tanpa itu garisnya hanya bergerak setelah PATCH-nya pulang, jadi ia
   * tertinggal di belakang jari — dan sebuah top dipilih dengan memandang
   * garis itu duduk di tempatnya, bukan dengan menunggu.
   */
  const [markerMenu, setMarkerMenu] =
    useState<{ x: number; y: number; id?: number; md: number; track?: string } | null>(null);
  const [markerDraft, setMarkerDraft] = useState<MarkerDraft | null>(null);
  const [dragged, setDragged] = useState<{ id: number; md: number } | null>(null);
  const marker = (id: number | undefined) => markers.find((m) => m.id === id);
  const actOnMarker = markerActions(onDataChanged, toast);

  /**
   * Lembar yang dipegang tangan: pembungkus track, bukan panel yang bergulir.
   *
   * Tingginya dibaca oleh overlay kotak zoom, yang menggambar dalam piksel isi
   * lembar dan bukan piksel yang terlihat.
   */
  const sheetRef = useRef<HTMLDivElement | null>(null);
  // MD, TVD or TVDSS. On a vertical well the three nearly coincide; on
  // 31/2-19 S they differ by 443 m at total depth, and reading a marker off the
  // wrong one puts it in the wrong bed.
  // Per proyek, dan bawaannya datang dari proyek itu sendiri: MD hampir selalu
  // benar, tetapi lapangan yang meja putarnya berbeda-beda tinggi hanya bisa
  // dibaca pada TVDSS, dan lapangan seperti itu tidak boleh menunggu tiap
  // pembacanya menemukan sendiri bahwa lembarnya dibuka pada acuan yang keliru.
  const [depthMode, setDepthMode] = useStored<DepthReference>(
    `evolog.log.depth.${project.id}`, project.default_depth_reference ?? "md");
  const [showGrid, setShowGrid] = useStored("evolog.log.grid", true);
  // Angka picker di dalam track. Sebagian pekerjaan — memandang bentuk kurva,
  // menyiapkan tangkapan layar — lebih baik tanpa angka menempel di atasnya,
  // maka bacaannya bisa dipulangkan ke kaki layar saja.
  const [showReadings, setShowReadings] = useStored("evolog.log.readings", true);
  // Vertical grid lines per track and horizontal grid spacing. Log paper is
  // ruled to whatever the reader needs to count against, so this is a setting
  // rather than a constant.
  const [gridDivisions, setGridDivisions] = useStored("evolog.log.griddiv", 5);
  const [depthGrid, setDepthGrid] = useStored("evolog.log.depthgrid", 24);
  // Susunan track — track apa yang aktif, kurva apa di dalamnya, dan
  // bagaimana masing-masing digambar. Disimpan mentah lalu dibersihkan saat
  // dibaca: isinya berasal dari localStorage versi aplikasi mana pun yang
  // terakhir dipakai peramban ini.
  const [storedTracks, setStoredTracks] = useStored<unknown>("evolog.log.tracks", null);
  const trackConfig = useMemo(() => normalizeTracks(storedTracks) ?? defaultTracks(), [storedTracks]);
  const setTrackConfig = (next: TrackConfig[]) => setStoredTracks(next);

  // Lebar rel parameter. Tab Parameter puas dengan 288 piksel; tab Track —
  // dua kotak angka berdampingan, sebuah daftar kurva — tidak. Satu angka
  // tidak bisa benar untuk keduanya, jadi angkanya menjadi milik pengguna.
  const [railWidth, setRailWidth] = useStored("evolog.rail.width", 330);
  // Pohon proyek. Nama sumur Laut Utara seperti "35/11-13" muat pada 246
  // piksel; nama set kurva beserta versinya sering tidak, dan berakhir sebagai
  // elipsis yang menyembunyikan justru bagian yang membedakannya.
  const [treeWidth, setTreeWidth] = useStored("evolog.tree.width", 246);
  const [weight, setWeight] = useStored("evolog.log.weight", 1.3);
  const [tab, setTab] = useState<RailTab>("parameters");
  // Rel yang terlipat, dan lebar yang ditinggalkannya. Disimpan sebab rel yang
  // terbuka lagi setiap kali layar dibuka memaksa penggunanya melipatnya ulang
  // setiap pagi — alasan yang sama dengan lebarnya sendiri.
  const [railCollapsed, setRailCollapsed] = useStored("evolog.rail.collapsed", false);
  // Pohon proyek pun begitu: ia dipakai untuk berpindah sumur dan mencari
  // kurva, dan sesudah kurvanya terpasang di track ia menahan 246 piksel di
  // tepi kiri lembar selama sisa pagi itu.
  const [treeCollapsed, setTreeCollapsed] = useStored("evolog.tree.collapsed", false);

  /**
   * Membuka sebuah tab, dan membuka relnya bila ia sedang terlipat.
   *
   * Dipakai di mana pun tab dipilih dari luar rel — menekan kepala track di
   * lembar log, misalnya. Tanpa ini, "Setel track" pada rel yang terlipat
   * memindahkan tab yang tidak terlihat oleh siapa pun: sebuah tombol yang
   * ditekan dan tidak menghasilkan apa-apa di layar.
   */
  const openTab = (key: RailTab) => {
    setTab(key);
    setRailCollapsed(false);
  };
  const [cursor, setCursor] = useState<number | null>(null);
  // Kurva yang sedang diperiksa isinya. Dipegang di layar dan bukan di pohon
  // proyek supaya jendelanya berdiri di atas seluruh lembar, bukan terjepit di
  // dalam rel selebar dua ratus piksel.
  const [inspect, setInspect] = useState<{
    id: number;
    mnemonic: string;
    color: string;
    /** Kurva bernama sama di set lain, untuk dibandingkan berdampingan. */
    versions: { id: number; label: string }[];
    /** Kedalaman yang dituju, bila jendelanya dibuka dari lembar log. */
    focusMd?: number;
  } | null>(null);
  // Picker yang dikunci. Sebuah bacaan yang lenyap begitu tetikus bergerak ke
  // panel parameter tidak bisa disalin, dibandingkan, atau dibacakan kepada
  // orang lain — klik pada track mengunci kedalamannya, klik lagi melepasnya.
  const [pinned, setPinned] = useState<number | null>(null);
  const [selectedTrack, setSelectedTrack] = useState<string | null>(null);
  const [running, setRunning] = useState(false);
  const [overrides, setOverrides] = useState<Partial<ParameterValues>>({});
  const [activeZone, setActiveZone] = useState<string | null>(null);

  // Kedalaman terkunci milik sumur tempat ia dikunci. Membawanya ke sumur
  // berikutnya berarti sebuah garis bidik di kedalaman yang tidak pernah
  // dipilih siapa pun di sana — dan pada acuan TVDSS, di lapisan yang lain
  // lagi.
  useEffect(() => setPinned(null), [well.id, depthMode]);

  const zones = well.zones;
  const zone = zones.find((z) => z.name === activeZone) ?? zones[0] ?? null;

  // Plot window: the active zone plus 25 m of context, or the whole well.
  const [top, base] = useMemo<[number, number]>(() => {
    if (zone) return [zone.top_md - 25, zone.base_md + 25];
    const first = well.curve_sets[0];
    return first ? [0, 0] : [0, 0];
  }, [zone, well]);

  const logs = useAsync(
    (signal) =>
      api.get<LogsPayload>(
        `/api/wells/${well.id}/logs/`,
        { top: top || undefined, base: base || undefined, decimate: 2500,
          depth: depthMode },
        signal,
      ),
    [well.id, top, base, depthMode, dataVersion],
  );

  // Marker digambar dari muatan log, bukan dari prop sumurnya: yang di prop
  // selalu MD, sedangkan sumbu lembar mengikuti acuan yang dipilih. Pada MD
  // keduanya sama persis; pada TVDSS di Teapot Dome keduanya berselisih
  // sepanjang tinggi meja putarnya, 1,6 km.
  const markers = useMemo(() => {
    const source = logs.data?.well.markers ?? well.markers;
    return source.map((m) => (dragged && dragged.id === m.id ? { ...m, md: dragged.md } : m));
  }, [logs.data, well.markers, dragged]);

  // Marker disunting pada MD, sebab MD yang disimpan. Menyeretnya di atas
  // sumbu turunan akan menuliskan angka sumbu itu sebagai MD — pada Teapot
  // sebuah top berpindah ribuan meter dalam satu seretan, dan yang tersimpan
  // tetap terbaca sebagai kedalaman yang sah.
  const mayEditMarkers = mayWrite && depthMode === "md";

  // Warna arsiran bawaan proyek. Track yang sudah membawa warnanya sendiri
  // tetap memakainya — lihat `TrackEditor`, prop `shadingDefault`.
  const logColors = useAsync(
    (signal) => api.get<LogColorPayload>(logColorsUrl(project.id), undefined, signal),
    [project.id],
  );

  const parameterSets = useAsync(
    (signal) =>
      api
        .get<Page<ParameterSet>>("/api/parameter-sets/", { project: project.id, latest: 1 }, signal)
        .then((page) => unpage(page, signal)),
    [project.id],
  );

  const runs = useAsync(
    (signal) =>
      api
        .get<Page<AnalysisRun>>("/api/runs/", { well: well.id, kind: "deterministic" }, signal)
        .then((page) => unpage(page, signal)),
    [well.id],
  );

  const [parameterSetId, setParameterSetId] = useState<number | null>(null);
  const parameterSet =
    parameterSets.data?.find((set) => set.id === parameterSetId) ?? parameterSets.data?.[0] ?? null;
  const params: ParameterValues | null = parameterSet
    ? ({ ...parameterSet.resolved, ...overrides } as ParameterValues)
    : null;

  const curveByName = useMemo(() => {
    const map = new Map<string, CurveData>();
    for (const curve of logs.data?.curves ?? []) map.set(curve.mnemonic, curve);
    return map;
  }, [logs.data]);

  // [atas, bawah] sebagaimana keduanya jatuh di lembar, bukan
  // [terkecil, terbesar]: pada ELEV angka terbesar ada di kepala, dan jendela
  // yang tetap menaik menggambar sumurnya terbalik. Arahnya diambil dari
  // muatan, bukan dari `depthMode`, supaya jendelanya tidak pernah menyilang
  // acuan lain selama muatan berikutnya masih dalam perjalanan.
  const depthRange = useMemo<[number, number] | null>(() => {
    const curves = logs.data?.curves ?? [];
    const depths = curves.flatMap((curve) => (curve.depths.length ? [curve.depths[0], curve.depths[curve.depths.length - 1]] : []));
    if (!depths.length) return null;
    return orientRange(Math.min(...depths), Math.max(...depths),
                       logs.data?.depth_reference ?? "md");
  }, [logs.data]);

  const depthSpan = depthRange ? axisSpan(depthRange[0], depthRange[1]) : 0;

  /**
   * Roda, mistar dan kotak — tangan yang sama dengan lembar korelasi.
   *
   * Kepala track lembar ini duduk di luar panel yang bergulir, jadi `headerH`
   * nol: kedalaman di puncak panel memang kedalaman yang tergambar di sana.
   * `panX` menyalakan sumbu mendatarnya, sebab lembar log boleh lebih lebar
   * daripada panelnya — track kesembilan tidak boleh memampatkan delapan
   * lainnya sampai tidak ada satu pun yang terbaca.
   */
  const {
    attachPane,
    scrollX,
    sheet,
    scaleMode,
    setScaleMode,
    stretch,
    boxHandlers,
    box,
    consumePick,
  } = useDepthSheet({
    storageKey: "evolog.log.scale",
    metres: depthSpan,
    range: depthRange,
    panX: true,
  });

  /**
   * Menyunting satu kurva dari kepala tracknya.
   *
   * Menulis ke susunan yang sama dengan penyunting di rel kanan — keduanya
   * memanggil `updateCurve` — sehingga tidak ada dua sumber kebenaran yang
   * bisa berselisih tentang warna sebuah kurva.
   */
  /**
   * Suntingan dari kepala track, dialamatkan ke kurva yang benar-benar diklik.
   *
   * Yang datang adalah `TrackCurve.key`, dan di lembar ini isinya kedudukan
   * kurva di dalam `config.curves`. Dulu yang datang adalah namanya, dicari
   * kembali dengan `findIndex` — dan pencarian itu selalu berhenti di kurva
   * pertama, sehingga susunan yang memuat satu mnemonic dua kali hanya bisa
   * menyunting yang pertama betapapun yang diklik yang kedua.
   *
   * Kedudukan itu kedudukan di dalam susunan tersimpan, bukan di dalam track
   * yang tergambar: kurva yang disembunyikan dan kurva yang datanya tidak ada
   * di sumur ini dibuang sebelum digambar, jadi keduanya berselisih.
   */
  const editCurve = (trackId: string, curveKey: string, patch: Partial<CurveConfig>) => {
    const track = trackConfig.find((entry) => entry.id === trackId);
    const index = Number(curveKey);
    if (!track || !Number.isInteger(index) || index < 0 || index >= track.curves.length) return;
    setTrackConfig(updateCurve(trackConfig, trackId, index, patch));
  };

  const editSpectrum = (trackId: string, patch: Partial<SpectrumConfig>) => {
    const track = trackConfig.find((entry) => entry.id === trackId);
    if (!track?.spectrum) return;
    setTrackConfig(updateTrack(trackConfig, trackId, { spectrum: { ...track.spectrum, ...patch } }));
  };

  /**
   * Susunan yang tersimpan, ditemukan dengan kurva yang benar-benar ada.
   *
   * Track yang seluruh kurvanya tidak dimiliki sumur ini tidak digambar sama
   * sekali: sebuah kolom kosong selebar satu track pada lembar sumur yang tak
   * pernah dilog sonik hanya memakan ruang yang dibutuhkan kurva lain. Tetapi
   * setelannya tetap tersimpan, sehingga berpindah ke sumur yang punya DTC
   * mengembalikan track itu apa adanya.
   */
  const tracks = useMemo<TrackSpec[]>(() => {
    return trackConfig
      .map((config): TrackSpec => {
        const logarithmic = config.scale === "log";
        const curves = config.curves
          // Kedudukan diambil sebelum penyaringan: ia harus menunjuk kurva di
          // dalam susunan tersimpan, sebab ke sanalah suntingan dituliskan.
          .map((spec, index) => ({ spec, index }))
          .filter(({ spec }) => !spec.hidden)
          .map(({ spec, index }): TrackCurve | null => {
            const data = curveByName.get(spec.name);
            if (!data) return null;
            return toTrackCurve(data, {
              key: String(index),
              color: spec.color ?? defaultCurveColor(spec.name, data.color),
              style: spec.style ?? "solid",
              width: spec.width,
              customScale: spec.min !== undefined || spec.max !== undefined,
              logarithmic,
              // Arsirannya diteruskan apa adanya, warna kosong dan semuanya:
              // yang menggambar tahu bahwa kosong berarti warna kurva, dan
              // yang menyunting harus tetap bisa membedakan keduanya.
              fill: spec.fill,
              ...(spec.min !== undefined ? { min: spec.min } : {}),
              ...(spec.max !== undefined ? { max: spec.max } : {}),
            });
          })
          .filter((curve): curve is TrackCurve => curve !== null);
        // Pita warna, dengan jepitan yang sudah pasti berupa angka. Kurva yang
        // disebut setelan pita boleh saja tidak ada di sumur ini — kurva
        // pertama track lalu yang diwarnai, sebab track yang mendadak kosong
        // karena satu nama tidak cocok adalah kegagalan yang tak terbaca.
        const painted = config.spectrum
          ? (curves.find((curve) => curve.name === config.spectrum!.curve) ?? curves[0])
          : undefined;
        return {
          id: config.id,
          title: config.title,
          units: config.units,
          flex: config.flex,
          logarithmic,
          lithology: config.lithology,
          described: config.described,
          classes: config.classes,
          shading: config.shading,
          spectrum:
            config.spectrum && painted
              ? {
                  curve: painted.name,
                  ramp: config.spectrum.ramp,
                  reverse: config.spectrum.reverse === true,
                  lo: config.spectrum.min ?? painted.min,
                  hi: config.spectrum.max ?? painted.max,
                  bars: config.spectrum.bars === true,
                  custom: config.spectrum.min !== undefined || config.spectrum.max !== undefined,
                }
              : undefined,
          curves,
        };
      })
      .filter((track) => track.curves.length > 0 || track.lithology || track.described
        || track.classes);
  }, [curveByName, trackConfig]);

  /** Vsh drives a lithology column: shale, silty sand, clean sand. */
  const lithologyBlocks = useMemo(
    () => lithologyFromVsh(curveByName.get("VSH")),
    [curveByName],
  );

  /**
   * Litologi yang dideskripsi — core atau cutting — dan bukan diturunkan dari
   * kurva, sebagaimana [describedLithology] menyusunnya.
   */
  const mintaDeskripsi = trackConfig.some((track) => track.described);
  const descriptions = useAsync(
    (signal) =>
      api
        .get<Page<CoreDescription>>("/api/core-descriptions/", { well: well.id }, signal)
        .then((page) => unpage(page, signal))
        // Muatan membawa sumur asalnya. `useAsync` menahan data lama selama
        // permintaan berikutnya berjalan, jadi tanpa cap ini kolom deskripsi
        // menggambar selang sumur sebelumnya di atas sumur yang sedang dibuka
        // — batuan yang salah, tergambar rapi, pada sumur yang salah.
        .then((rows) => ({ wellId: well.id, rows })),
    [well.id, dataVersion],
    // Sebuah sumur bisa membawa tujuh ratus selang cutting, dan tidak ada
    // gunanya mengunduhnya untuk lembar yang tidak menggambarkannya.
    { skip: !mintaDeskripsi },
  );

  /**
   * Deskripsi yang benar-benar milik sumur ini, atau `null` selagi belum
   * kembali.
   *
   * Bedanya dengan larik kosong adalah bedanya "belum tahu" dan "tidak ada":
   * yang kedua tercetak di dalam tracknya sebagai keterangan bahwa sumur ini
   * memang tidak punya deskripsi, dan keterangan itu tidak boleh berkedip
   * sekejap pada sumur yang deskripsinya lengkap.
   */
  const describedRows =
    descriptions.data?.wellId === well.id ? descriptions.data.rows : null;

  const describedBlocks = useMemo(
    () => describedLithology(describedRows ?? []),
    [describedRows],
  );

  /**
   * Pita kelas LRLC, dari kurva `LRLC_CLASS` yang sudah diterbitkan.
   *
   * Dibaca dari kurva dan bukan dari layar LRLC: yang tergambar di lembar ini
   * adalah hasil yang **tersimpan**, sama seperti kurva lain. Sebuah kolom
   * yang menggambar hitungan yang belum diterbitkan akan berubah isinya
   * begitu seseorang menggeser penggeser di layar sebelah, tanpa ada yang
   * menyimpan apa yang tadi terlihat.
   */
  const lrlcBlocks = useMemo(() => {
    const curve = (logs.data?.curves ?? [])
      .find((c) => c.mnemonic.toUpperCase() === "LRLC_CLASS");
    return curve ? classBlocks(curve.depths, curve.values) : [];
  }, [logs.data]);

  /** Kedalaman yang sedang dibaca picker: yang dikunci mengalahkan yang dilewati. */
  const reading = pinned ?? cursor;

  /**
   * Baris bacaan di kaki layar, diambil dari track yang benar-benar aktif.
   *
   * Sebelumnya daftarnya adalah lima mnemonic tetap. Itu berarti seorang
   * pengguna yang baru saja menambahkan track DTC melihat kurvanya tergambar
   * tetapi tidak pernah melihat angkanya, dan sebuah picker yang menolak
   * membaca kurva yang barusan diminta bukanlah picker.
   */
  const readout = useMemo(() => {
    if (reading === null) return [];
    const seen = new Set<string>();
    const rows: { name: string; value: number; unit?: string }[] = [];
    for (const track of tracks) {
      for (const curve of track.curves) {
        if (seen.has(curve.name)) continue;
        const value = sampleAt(curve, reading);
        if (value === null) continue;
        seen.add(curve.name);
        rows.push({ name: curve.name, value, unit: curve.unit });
      }
    }
    return rows;
  }, [reading, tracks]);

  const setParam = (
    key: keyof ParameterValues,
    value: number | string | boolean | Record<string, string>,
  ) =>
    setOverrides((current) => ({ ...current, [key]: value }));

  const compute = async () => {
    if (!parameterSet) return;
    setRunning(true);
    try {
      const run = await api.post<AnalysisRun>("/api/compute/deterministic/", {
        well: well.id,
        parameter_set: parameterSet.id,
        overrides: Object.keys(overrides).length ? overrides : undefined,
        top: zone ? zone.top_md : undefined,
        base: zone ? zone.base_md : undefined,
      });
      const netPay = run.summary.total?.net_pay;
      toast(`Computation done — net pay ${netPay ?? "—"} m (${run.duration_ms} ms).`);
      setOverrides({});
      setParameterSetId(run.parameter_set);
      parameterSets.reload();
      runs.reload();
      logs.reload();
      onDataChanged();
    } catch (cause) {
      toast(cause instanceof Error ? cause.message : String(cause), "error");
    } finally {
      setRunning(false);
    }
  };

  /**
   * Item menu "sunting nilai" untuk track yang sedang ditunjuk.
   *
   * Satu item per kurva di track itu, sebab track RHOB/NPHI memuat dua kurva
   * pada satu kotak dan menu yang menyebut salah satunya saja akan menyunting
   * yang tidak diminta separuh waktunya. Kurva yang tidak ada di sumur ini
   * tidak muncul: yang tergambar adalah yang ada.
   */
  const editItemsFor = (trackId: string | undefined, md: number) => {
    const track = trackConfig.find((candidate) => candidate.id === trackId);
    if (!track) return [];
    return track.curves
      .map((spec) => curveByName.get(spec.name))
      .filter((data): data is CurveData => data !== undefined)
      .map((data) => ({
        label: `Edit the ${data.mnemonic} value at ${md.toFixed(2)} m…`,
        onClick: () =>
          setInspect({
            id: data.id,
            mnemonic: data.mnemonic,
            color: defaultCurveColor(data.mnemonic, data.color),
            versions: [],
            focusMd: md,
          }),
      }));
  };

  const latestRun = runs.data?.[0] ?? null;
  const dirty = Object.keys(overrides).length > 0;

  return (
    <>
    <div style={{ display: "flex", alignItems: "stretch", flex: 1, minHeight: 0 }}>
      {/* Project tree */}
      {treeCollapsed ? (
        <RailStrip
          side="left"
          tabs={TREE_TABS}
          active="project"
          onSelect={() => setTreeCollapsed(false)}
          expandLabel="Show the project tree"
        />
      ) : (
        <>
          <aside
            style={{
              width: treeWidth,
              flex: "none",
              background: "var(--rail)",
              display: "flex",
              flexDirection: "column",
              minWidth: 0,
            }}
          >
            <ProjectTree
              onCurveInspect={setInspect}
              project={project}
              well={well}
              curveCount={logs.data?.curves.length ?? 0}
              zones={zones}
              activeZone={zone?.name ?? null}
              onZoneSelect={setActiveZone}
              onDataChanged={onDataChanged}
              depthRange={depthRange}
              onCollapse={() => setTreeCollapsed(true)}
            />
          </aside>
          {/* Seperti di rel kanan: pegangan lebarnya hanya ada selama ada yang
              bisa dilebarkan. */}
          <Resizer
            width={treeWidth}
            onChange={setTreeWidth}
            min={190}
            max={460}
            side="left"
            label="Resize the project tree"
          />
        </>
      )}

      {/* Log display */}
      <section
        style={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          background: "var(--canvas)",
          // Lembar boleh lebih lebar daripada kolom ini, dan baris kepala track
          // digeser mendatar bersamanya alih-alih membawa batang gulir kedua.
          // Yang menahan tumpahannya adalah kolom ini: tanpa kliping di sini,
          // kepala track yang tergeser tergambar di atas pohon proyek dan rel
          // parameter — dan yang terbaca bukan "lembar ini digeser" melainkan
          // "layar ini rusak".
          overflow: "hidden",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 9,
            height: 30,
            flex: "none",
            padding: "0 11px",
            background: "var(--toolbar)",
            borderBottom: "1px solid var(--line-soft)",
          }}
        >
          <span style={{ font: "500 11px var(--font-sans)", color: "var(--ink)" }}>
            {well.name} — Composite Log
          </span>
          <span style={{ font: "400 10px var(--font-mono)", color: "var(--ink-6)" }}>
            {depthRange
              ? `${logs.data?.depth_label ?? "MD"} ${units.formatDepth(depthRange[0])}–${units.formatDepth(depthRange[1])}`
              : "—"} · scale 1:{sheet.scale}{sheet.fitted ? " (fit)" : ""}
            {logs.data && logs.data.depth_reference !== "md"
              && logs.data.depth_source !== "survey"
              && " · no survey: assumed vertical"}
            {/* Sebuah TVD yang diam-diam diukur dari meja putar menggeser tiap
                kedalaman setinggi lantai bor, dan tidak ada apa pun di lembarnya
                yang memperlihatkan itu. Jadi lembarnya yang mengatakannya. */}
            {logs.data?.depth_caveat && " · TVD from the rotary table: no ground elevation"}
          </span>
          <span style={{ flex: 1 }} />
          <Dropdown
            value={depthMode}
            options={DEPTH_ORDER.map((value) => ({
              value,
              label: DEPTH_LABEL[value],
              hint: DEPTH_HINT[value],
            }))}
            onChange={(value) => setDepthMode(value as DepthReference)}
            width={92}
            compact
          />
          {/* Vertical stretch. The four standard scales are one click away, but
              the buttons either side step continuously — reading a thin bed
              often needs 1:150, and a fixed list of four cannot offer it. */}
          <Button
            variant="ghost"
            title="Stretch vertically (mouse wheel over the sheet)"
            onClick={() => setScaleMode(stepScale(sheet.scale, 1))}
          >
            +
          </Button>
          <Button
            variant="ghost"
            title="Compress vertically (mouse wheel over the sheet)"
            onClick={() => setScaleMode(stepScale(sheet.scale, -1))}
          >
            −
          </Button>
          <Button variant="ghost" title="Fit the whole well into the panel height"
                  onClick={() => setScaleMode("fit")}>
            Fit
          </Button>
          <Dropdown
            value={scaleMode === "fit" ? "fit" : String(sheet.scale)}
            options={[
              { value: "fit", label: "Fit" },
              ...[...new Set([...DEPTH_SCALE_PRESETS, ...(sheet.fitted ? [] : [sheet.scale])])]
                .sort((a, b) => a - b)
                .map((step) => ({ value: String(step), label: `1:${step}` })),
            ]}
            onChange={(value) => setScaleMode(value === "fit" ? "fit" : Number(value))}
            width={84}
            compact
          />
        </div>

        {logs.loading && <Loading label="Loading curves…" />}
        {logs.error && (
          <div style={{ padding: 16 }}>
            <Notice tone="error">{logs.error}</Notice>
          </div>
        )}
        {/* Digantung pada depthRange, bukan pada tracks.length: defaultTracks()
            selalu menyisakan kolom litologi, yang lolos dari saringan track
            kosong, sehingga tracks.length tidak pernah nol dan pemberitahuan
            ini tidak pernah muncul — lembarnya cuma memutih. Tanpa kurva
            tidak ada depthRange, dan itulah keadaan "tak ada yang bisa
            digambar" yang sebenarnya. */}
        {!logs.loading && !logs.error && !depthRange && (
          <Empty>
            {/* Sumur rencana bukan sumur yang datanya hilang. Saran "impor LAS"
                mengirim pembacanya mencari berkas yang tidak pernah ada, sebab
                belum ada yang mengukur apa pun di sana. Yang bisa dilakukan
                sekarang adalah menyusun kurva prognosis dari sumur tetangga,
                jadi itu yang disebut. */}
            {well.status === "planned" ? (
              <>
                {well.name} adalah sumur rencana — belum dibor.
                <span style={{ font: "400 11px var(--font-mono)" }}>
                  QC &amp; Edit Data › Curve insert bisa menyusun kurva prognosis untuk
                  sementara; kurva terukurnya ada setelah sumur ini dibor.
                </span>
              </>
            ) : (
              <>
                {well.name} has no curves yet.
                <span style={{ font: "400 11px var(--font-mono)" }}>
                  Import a LAS file from the File menu.
                </span>
              </>
            )}
          </Empty>
        )}

        {tracks.length > 0 && depthRange && (
          <>
            <TrackHeaders
              depthUnit={`${logs.data?.depth_label ?? "MD"} ${units.depthUnit}`}
              tracks={tracks}
              gridDivisions={gridDivisions}
              selected={selectedTrack}
              weight={weight}
              onSelect={(id) => {
                setSelectedTrack(id === selectedTrack ? null : id);
                openTab("tracks");
              }}
              scrollX={scrollX}
              onCurveEdit={editCurve}
              onCurveAuto={(trackId, key) => editCurve(trackId, key, { min: undefined, max: undefined })}
              onSpectrumEdit={editSpectrum}
              onSpectrumRemove={(trackId) =>
                setTrackConfig(updateTrack(trackConfig, trackId, { spectrum: undefined }))
              }
              // Menyeret kepala track memindahkannya, bukan menukarnya dengan
              // yang di bawah tapak: track yang dilewati bergeser satu tempat
              // masing-masing, sebagaimana kartu yang disisipkan di tengah
              // tumpukan. Tombol kiri/kanan di tab Track tetap ada — satu
              // tekanan, satu langkah — dan keduanya menulis susunan yang sama.
              onReorder={(id, targetId) => setTrackConfig(moveTrackTo(trackConfig, id, targetId))}
            />
            <div
              ref={attachPane}
              className="scroll-y"
              style={{ flex: 1, minHeight: 0, overflowY: "auto", overflowX: "auto" }}
            >
            <div
              style={{
                display: "flex",
                alignItems: "stretch",
                // Mode pas memakai tinggi panel apa adanya, sehingga tidak ada
                // batang gulir setinggi dua piksel karena pembulatan skala.
                height: sheet.fitted ? "100%" : sheet.heightPx,
                // Lembar boleh lebih lebar daripada panelnya. Tanpa ini,
                // menambah track kesembilan memampatkan delapan lainnya
                // sampai tidak ada satu pun yang terbaca, dan melebarkan satu
                // track selalu berarti menyempitkan yang lain.
                minWidth: DEPTH_GUTTER_W + tracksMinWidth(tracks),
              }}
            >
              <DepthGutter
                top={depthRange[0]}
                base={depthRange[1]}
                ticks={depthTickCount(sheet.heightPx)}
                cursor={reading}
                cursorLabel={(depth) => units.formatDepth(depth)}
                onStretch={stretch}
                // Mistar tetap di tepi kiri sepanjang geseran mendatar: sebuah
                // kurva tanpa kedalaman di sebelahnya adalah gambar, bukan log.
                style={{ position: "sticky", left: 0, zIndex: 2 }}
              />
              {/* Track dikurung sendiri, terpisah dari mistar: seret pada
                  keduanya berarti dua hal yang berbeda — meregang di mistar,
                  memilih interval di lembar — dan pembatasnya harus berupa
                  elemen, bukan pemeriksaan sasaran kejadian. */}
              <div
                ref={sheetRef}
                {...boxHandlers}
                // Klik kanan di lembar menanyakan kedalaman tempat jari
                // menekan, bukan kedalaman kursor terakhir: keduanya berbeda
                // ketika menu dibuka tanpa menyapu lembarnya lebih dulu.
                onContextMenu={(event) => {
                  if (!depthRange) return;
                  event.preventDefault();
                  const rect = event.currentTarget.getBoundingClientRect();
                  const fraction = (event.clientY - rect.top) / rect.height;
                  setMarkerMenu({
                    x: event.clientX,
                    y: event.clientY,
                    md: pickedDepth(depthRange[0] + fraction * (depthRange[1] - depthRange[0])),
                    // Track yang ditunjuk, supaya menunya bisa menawarkan
                    // penyuntingan kurva yang memang ada di bawah jari — dan
                    // bukan kurva pertama lembar ini.
                    track:
                      (event.target as HTMLElement).closest("[data-track-id]")
                        ?.getAttribute("data-track-id") ?? undefined,
                  });
                }}
                style={{
                  position: "relative",
                  flex: 1,
                  minWidth: 0,
                  display: "flex",
                  alignItems: "stretch",
                  touchAction: box ? "none" : undefined,
                  userSelect: box ? "none" : undefined,
                }}
              >
                {tracks.map((track) => (
                  <LogTrack
                    key={track.id}
                    track={track}
                    top={depthRange[0]}
                    base={depthRange[1]}
                    zones={zones.map((z) => ({ name: z.name, top: z.top_md, base: z.base_md, color: z.color }))}
                    markers={markers.map((m) => ({ name: m.name, md: m.md, color: m.color }))}
                    showGrid={showGrid}
                    gridDivisions={gridDivisions}
                    depthGrid={depthGrid}
                    weight={weight}
                    lithologyBlocks={track.lithology ? lithologyBlocks : undefined}
                    classBlocks={track.classes ? lrlcBlocks : undefined}
                    describedBlocks={
                      // Selama permintaannya belum kembali, track ini tidak
                      // membawa larik sama sekali. Larik kosong di sini
                      // berarti "sumur ini memang tidak punya deskripsi", dan
                      // itulah yang tercetak di dalam tracknya — sebuah
                      // keterangan yang muncul sekejap selagi memuat akan
                      // berbohong tentang sumur yang datanya lengkap.
                      track.described && describedRows ? describedBlocks : undefined
                    }
                    onHoverDepth={(depth) => setCursor(depth)}
                    cursorDepth={reading}
                    showReadings={showReadings}
                    onPickDepth={(depth) => {
                      // Klik yang menyusul seretan — kotak zoom atau geseran
                      // mendatar — bukan permintaan mengunci picker di tempat
                      // jari berhenti.
                      if (consumePick()) return;
                      // Klik kedua di tempat yang sama melepas kuncian. Ambangnya
                      // diukur dalam piksel layar, bukan dalam meter batuan:
                      // empat meter adalah "tempat yang sama" pada 1:5000 dan
                      // sejengkal jauhnya pada 1:50.
                      setPinned((current) =>
                        current !== null && Math.abs(current - depth) * sheet.pxPerMetre < 4
                          ? null
                          : depth,
                      );
                    }}
                  />
                ))}
                {depthRange && (
                  <MarkerHandles
                    markers={markers}
                    top={depthRange[0]}
                    base={depthRange[1]}
                    mayWrite={mayEditMarkers}
                    onDrag={(id, md, done) => {
                      setDragged({ id, md: pickedDepth(md) });
                      if (!done) return;
                      setDragged(null);
                      const moved = marker(id);
                      const settled = pickedDepth(md);
                      if (moved && Math.abs(moved.md - settled) > 1e-6) {
                        void actOnMarker.move(id, moved.name, settled);
                      }
                    }}
                    onMenu={(event, id) =>
                      setMarkerMenu({
                        x: event.clientX,
                        y: event.clientY,
                        id,
                        md: marker(id)?.md ?? 0,
                      })
                    }
                  />
                )}
                {box && depthRange && (
                  <BoxZoomOverlay
                    from={box.from}
                    to={box.to}
                    height={sheetRef.current?.clientHeight ?? 1}
                    top={depthRange[0]}
                    base={depthRange[1]}
                    format={(depth) => units.formatDepth(depth)}
                  />
                )}
              </div>
            </div>
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 16,
                height: 26,
                flex: "none",
                padding: "0 11px",
                background: "var(--toolbar)",
                borderTop: "1px solid var(--line)",
                font: "400 10px var(--font-mono)",
                color: "var(--ink-4)",
              }}
            >
              <span style={{ color: pinned !== null ? "var(--accent)" : undefined, whiteSpace: "nowrap" }}>
                {pinned !== null ? "pinned" : "cursor"}{" "}
                {reading !== null ? units.formatDepth(reading) : "—"}
              </span>
              {pinned !== null && (
                <button
                  type="button"
                  onClick={() => setPinned(null)}
                  title="Lepas kuncian picker"
                  style={{
                    border: "1px solid var(--border-control)",
                    borderRadius: 3,
                    background: "transparent",
                    color: "var(--ink-5)",
                    font: "400 9px var(--font-mono)",
                    padding: "0 4px",
                    cursor: "pointer",
                    flex: "none",
                  }}
                >
                  lepas
                </button>
              )}
              <span style={{ display: "flex", gap: 14, overflow: "hidden" }}>
                {readout.map((entry) => (
                  <span key={entry.name} style={{ whiteSpace: "nowrap" }}>
                    {entry.name} {formatReading(entry.value)}
                    {entry.unit ? ` ${entry.unit}` : ""}
                  </span>
                ))}
              </span>
              <span style={{ flex: 1 }} />
              {/* Kendali yang tak terlihat sama saja dengan tidak ada. Roda
                  yang memperbesar alih-alih menggulir adalah kejutan bagi
                  siapa pun yang belum pernah memakainya, dan satu baris di
                  kaki layar lebih murah daripada seorang pengguna yang
                  menyimpulkan bahwa lembarnya rusak. */}
              <span style={{ color: "var(--ink-6)", whiteSpace: "nowrap" }}>
                wheel: zoom · drag the ruler: stretch · drag the sheet: vertical zooms, horizontal pans
              </span>
              {latestRun && (
                <span style={{ color: dirty ? "var(--amber)" : "var(--accent)" }}>
                  ● {dirty ? "parameters changed — needs recomputing" : "computation up to date"}
                </span>
              )}
            </div>
          </>
        )}
      </section>

      {/* Parameter rail */}
      {/* Pegangan lebarnya hanya ada selama ada yang bisa dilebarkan. Sebuah
          pemisah yang bisa diseret di tepi pita selebar 30 piksel menjanjikan
          lebar yang tidak akan tampak sampai relnya dibuka lagi. */}
      {!railCollapsed && (
        <Resizer
          width={railWidth}
          onChange={setRailWidth}
          min={280}
          max={560}
          label="Resize the parameter panel"
        />
      )}
      {railCollapsed ? (
        <RailStrip
          tabs={RAIL_TABS}
          active={tab}
          onSelect={openTab}
          expandLabel="Show the parameter panel"
        />
      ) : (
        <aside
          style={{
            width: railWidth,
            flex: "none",
            background: "var(--rail)",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <RailTabBar
            tabs={RAIL_TABS}
            active={tab}
            onSelect={setTab}
            onCollapse={() => setRailCollapsed(true)}
            collapseLabel="Hide the parameter panel"
          />

          <div
            className="scroll-y"
            style={{ flex: 1, padding: "12px 13px", display: "flex", flexDirection: "column", gap: 13 }}
          >
            {tab === "parameters" && (
              <>
                {parameterSets.loading && <Loading />}
                {params && parameterSet && (
                  <>
                    <Panel title="Parameter set" meta={`v${parameterSet.version}`}>
                      <Dropdown
                        value={parameterSet.id}
                        options={(parameterSets.data ?? []).map((set) => ({
                          value: set.id,
                          label: set.name,
                          hint: `v${set.version}`,
                        }))}
                        onChange={(value) => {
                          setParameterSetId(value);
                          setOverrides({});
                        }}
                        width="100%"
                      />
                      {parameterSet.note && (
                        <span style={{ font: "400 10px/1.4 var(--font-sans)", color: "var(--ink-5)" }}>
                          {parameterSet.note}
                        </span>
                      )}
                    </Panel>

                    {/* Panel memangkas isinya (`overflow: hidden`) supaya
                        sudut-sudutnya tetap bulat, dan daftar enam belas
                        indikator yang terbuka di dalamnya terpotong empat baris
                        di bawah tombolnya — pilihan yang tidak bisa dicapai
                        sama saja dengan pilihan yang tidak ada. Di panel ini
                        saja pemangkasan itu dilepas, sehingga daftarnya boleh
                        menjulur ke atas panel di bawahnya. */}
                    <Panel title="Shale volume" style={{ overflow: "visible" }}>
                      <VshFields params={params} onChange={setParam} disabled={!mayCompute} />
                    </Panel>

                    <Panel title="Total porosity">
                      <PhitFields params={params} onChange={setParam} disabled={!mayCompute} />
                    </Panel>

                    {/* Dipisah dari panel di atasnya sebab keduanya memang dua
                        pilihan: keluarga kedua porositas efektif tidak membaca
                        PHIT sama sekali. Satu panel berisi dua medan bernama
                        "Metode" adalah panel yang tidak bisa dibaca. */}
                    <Panel title="Effective porosity">
                      <PhieFields params={params} onChange={setParam} disabled={!mayCompute} />
                    </Panel>

                    {/* Metodenya dan konstantanya satu panel, bukan dua. Menu
                        "Model saturasi" dulu duduk di puncak rel dan keempat
                        angkanya di panel "Konstanta" di bawahnya, jadi Rw yang
                        dibaca metode yang dipilih tampak sebagai setelan yang
                        berdiri sendiri — dan Rsh, Qv serta suhunya, yang hanya
                        dibaca sebagian metodenya, tidak muncul sama sekali.
                        Panel-panel ini kini berurut sebagaimana tahapnya
                        berjalan: serpih, porositas, saturasi, cutoff. */}
                    <Panel title="Water saturation">
                      <SwFields params={params} onChange={setParam} disabled={!mayCompute} />
                    </Panel>

                    {/* Ketiga angkanya belum pernah punya satu kotak pun di rel
                        ini. Yang ada hanyalah "Use core data as anchor" di panel
                        "Opsi" — sebuah kalibrasi yang ditawarkan tanpa korelasi
                        yang dikalibrasinya — dan kotak itu pindah ke sini, ke
                        satu-satunya tahap yang membacanya. */}
                    <Panel title="Permeability">
                      <PermFields params={params} onChange={setParam} disabled={!mayCompute} />
                    </Panel>

                    {/* Ketiganya dulu tiga penggeser: tiga kendali yang serupa
                        dan berjajar bagi dua ambang atas dan satu ambang bawah,
                        tanpa satu kata pun tentang mana yang menghasilkan net
                        reservoir dan mana yang memilih net pay darinya. Sebuah
                        cutoff juga angka yang datang dari sebuah crossplot dan
                        dikutip di laporan sampai dua desimal — penggeser tidak
                        bisa diketik, jadi 0,42 harus dikenai pada piksel yang
                        tepat. Dan `Slider` belum pernah punya keadaan nonaktif:
                        ketiganya satu-satunya kendali hitung di rel ini yang
                        masih bisa digeser oleh peran yang tidak boleh
                        menghitung. */}
                    <Panel title="Cutoff">
                      <CutoffFields params={params} onChange={setParam} disabled={!mayCompute} />
                    </Panel>

                    <Panel title="Options">
                      <Toggle
                        label="Heavy mud correction"
                        checked={Boolean(params.heavy_mud_correction)}
                        onChange={(v) => setParam("heavy_mud_correction", v)}
                        disabled={!mayCompute}
                      />
                      <Toggle
                        label="Show the uncertainty band"
                        checked={Boolean(params.show_uncertainty_band)}
                        onChange={(v) => setParam("show_uncertainty_band", v)}
                        disabled={!mayCompute}
                      />
                    </Panel>

                    {dirty && (
                      <Notice>
                        Parameters changed but not yet computed. Running the computation will publish{" "}
                        {parameterSet.name} v{parameterSet.version + 1}.
                      </Notice>
                    )}
                    {!mayCompute && (
                      <Notice>Your role on this project cannot run computations.</Notice>
                    )}

                    <div
                      style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: "auto", flex: "none" }}
                    >
                      <Button
                        variant="primary"
                        full
                        busy={running}
                        disabled={!mayCompute}
                        onClick={compute}
                      >
                        {zone ? `Compute ${zone.name}` : "Compute interval"}
                      </Button>
                      <Button variant="ghost" full disabled={!mayCompute}>
                        Save as a template
                      </Button>
                    </div>
                  </>
                )}
                {!parameterSets.loading && !parameterSet && (
                  <Notice>This project has no parameter set yet. Run a computation to create one.</Notice>
                )}
              </>
            )}

            {tab === "tracks" && (
              <TrackEditor
                tracks={trackConfig}
                onChange={setTrackConfig}
                resolved={tracks}
                available={logs.data?.curves ?? []}
                selected={selectedTrack}
                onSelect={setSelectedTrack}
                shadingDefault={logColors.data?.colors["log.shading.default"]}
                layout={{ onRestore: () => setTrackConfig(defaultTracks()), extras: true }}
              />
            )}

            {tab === "curves" && (
              <>
                <Panel title="Display" bodyStyle={{ padding: "11px", gap: 12 }}>
                  <Toggle label="Grid lines" checked={showGrid} onChange={setShowGrid} />
                  <Slider
                    label="Grid columns"
                    value={gridDivisions}
                    min={2}
                    max={10}
                    step={1}
                    onChange={(value) => setGridDivisions(Math.round(value))}
                    format={(value) => `${Math.round(value)} per track`}
                  />
                  <Slider
                    label="Grid rows"
                    value={depthGrid}
                    min={8}
                    max={64}
                    step={2}
                    onChange={(value) => setDepthGrid(Math.round(value))}
                    format={(value) => `${Math.round(value)} px`}
                  />
                  <Toggle label="Picker readings on the track" checked={showReadings} onChange={setShowReadings} />
                  <Slider
                    label="Ketebalan bawaan"
                    value={weight}
                    min={0.8}
                    max={3}
                    step={0.1}
                    onChange={setWeight}
                    format={(v) => `${v.toFixed(1)} px`}
                  />
                  <span style={{ font: "400 9.5px/1.45 var(--font-sans)", color: "var(--ink-6)" }}>
                    The axis, scale, colour and width of each curve are set in the Track tab.
                  </span>
                </Panel>
                <Panel title="Available curves" flush bodyStyle={{ padding: 0 }}>
                  {(logs.data?.curves ?? []).map((curve) => (
                    <div
                      key={curve.id}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        padding: "6px 8px 6px 11px",
                        borderBottom: "1px solid var(--line-soft)",
                      }}
                    >
                      <span
                        style={{
                          width: 12,
                          height: 0,
                          flex: "none",
                          borderTop: `2px solid ${defaultCurveColor(curve.mnemonic, curve.color)}`,
                        }}
                      />
                      <span style={{ font: "500 11px var(--font-mono)", color: "var(--ink-2)" }}>
                        {curve.mnemonic}
                      </span>
                      <span style={{ flex: 1 }} />
                      <span style={{ font: "400 9.5px var(--font-mono)", color: "var(--ink-6)" }}>
                        {curve.unit || "—"} · {curve.count}
                      </span>
                      <MiniButton
                        title={`Show ${curve.mnemonic} on a new track`}
                        onClick={() => {
                          setTrackConfig(addTrack(trackConfig, makeTrack(curve.mnemonic, curve.unit)));
                          setTab("tracks");
                          toast(`Track ${curve.mnemonic} added.`);
                        }}
                      >
                        {Icon.plus({ size: 10 })}
                      </MiniButton>
                    </div>
                  ))}
                </Panel>
                <Panel title="Curve sets">
                  {well.curve_sets.map((set) => (
                    <KeyValue
                      key={set.id}
                      k={
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                          {set.is_active && <StatusDot tone="ok" />}
                          {set.name}
                        </span>
                      }
                      v={`v${set.version} · ${set.curve_count}`}
                    />
                  ))}
                </Panel>
              </>
            )}

            {tab === "results" && (
              <>
                {runs.loading && <Loading />}
                {!runs.loading && !latestRun && <Notice>No computation for this well yet.</Notice>}
                {latestRun && (
                  <>
                    <Panel
                      title="Reservoir summation"
                      meta={`${latestRun.duration_ms} ms`}
                      bodyStyle={{ padding: 0 }}
                      flush
                    >
                      <table style={{ width: "100%", borderCollapse: "collapse" }}>
                        <thead>
                          <tr>
                            {["Zone", "Gross", "Net pay", "PHIE", "SW"].map((head, index) => (
                              <th
                                key={head}
                                style={{
                                  padding: "6px 8px",
                                  textAlign: index === 0 ? "left" : "right",
                                  font: "600 9px var(--font-mono)",
                                  color: "var(--ink-5)",
                                  letterSpacing: ".06em",
                                  borderBottom: "1px solid var(--line-strong)",
                                }}
                              >
                                {head}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {(latestRun.summary.zones ?? []).map((row) => (
                            <tr key={row.name}>
                              <td style={cellStyle("left")}>{row.name}</td>
                              <td style={cellStyle()}>{row.gross.toFixed(1)}</td>
                              <td style={cellStyle()}>{row.net_pay.toFixed(1)}</td>
                              <td style={cellStyle()}>{row.phie_avg?.toFixed(3) ?? "—"}</td>
                              <td style={cellStyle()}>{row.sw_avg?.toFixed(2) ?? "—"}</td>
                            </tr>
                          ))}
                          {latestRun.summary.total && (
                            <tr>
                              <td style={{ ...cellStyle("left"), fontWeight: 600 }}>Total</td>
                              <td style={{ ...cellStyle(), fontWeight: 600 }}>
                                {latestRun.summary.total.gross?.toFixed(1) ?? "—"}
                              </td>
                              <td style={{ ...cellStyle(), fontWeight: 600 }}>
                                {latestRun.summary.total.net_pay?.toFixed(1) ?? "—"}
                              </td>
                              <td style={{ ...cellStyle(), fontWeight: 600 }}>
                                {latestRun.summary.total.phie_avg?.toFixed(3) ?? "—"}
                              </td>
                              <td style={{ ...cellStyle(), fontWeight: 600 }}>
                                {latestRun.summary.total.sw_avg?.toFixed(2) ?? "—"}
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </Panel>

                    <Panel title="Stages">
                      {latestRun.steps.map((step) => (
                        <div key={step.n} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <StatusDot tone={step.ok ? "ok" : "warn"} />
                          <span style={{ font: "400 11px var(--font-sans)", color: "var(--ink-2)" }}>
                            {step.name}
                          </span>
                          <span style={{ flex: 1 }} />
                          <span
                            style={{
                              font: "400 9.5px var(--font-mono)",
                              color: "var(--ink-6)",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              maxWidth: 130,
                            }}
                            title={step.method}
                          >
                            {step.method}
                          </span>
                        </div>
                      ))}
                    </Panel>

                    {(latestRun.warnings ?? []).map((warning, index) => (
                      <Notice key={index}>{warning}</Notice>
                    ))}

                    <Panel title="Provenance">
                      <KeyValue k="Computed by" v={latestRun.created_by_detail?.display_name ?? "—"} mono={false} />
                      <KeyValue k="Computed at" v={new Date(latestRun.created_at).toLocaleString("en-US")} />
                      <KeyValue
                        k="Approved"
                        v={latestRun.approved_by_detail?.display_name ?? "not yet"}
                        mono={false}
                      />
                    </Panel>
                  </>
                )}
              </>
            )}
          </div>
        </aside>
      )}
    </div>

      {inspect && (
        <CurveInspector
          curveId={inspect.id}
          mnemonic={inspect.mnemonic}
          color={inspect.color}
          versions={inspect.versions}
          mayEdit={mayWrite}
          focusMd={inspect.focusMd}
          onClose={() => setInspect(null)}
          onSaved={({ version, points }) => {
            toast(`${inspect.mnemonic}: ${points} points saved as version ${version}.`);
            onDataChanged();
          }}
        />
      )}

      {markerMenu && (
        <ContextMenu
          x={markerMenu.x}
          y={markerMenu.y}
          title={
            markerMenu.id !== undefined
              ? `${marker(markerMenu.id)?.name} · ${markerMenu.md.toFixed(1)} m`
              : `${markerMenu.md.toFixed(1)} m MD`
          }
          onClose={() => setMarkerMenu(null)}
          items={
            markerMenu.id === undefined
              ? [
                  ...(mayEditMarkers
                    ? [
                        {
                          label: "Set a marker here…",
                          icon: Icon.markerAdd({ size: 11 }),
                          onClick: () => setMarkerDraft({ md: markerMenu.md }),
                        },
                      ]
                    : []),
                  // Menyunting angka dibuka dari tempat angka itu dipandang.
                  // Yang dibuka adalah jendela isi kurvanya pada kedalaman ini
                  // — bukan sebuah kotak yang langsung menulis — sebab
                  // suntingan disimpan sekaligus sebagai satu versi baru, dan
                  // sebuah kotak per angka akan melahirkan satu versi per
                  // angka.
                  ...(mayWrite ? editItemsFor(markerMenu.track, markerMenu.md) : []),
                ]
              : [
                  {
                    label: "Edit…",
                    onClick: () => {
                      const found = marker(markerMenu.id);
                      if (found) setMarkerDraft({ ...found });
                    },
                  },
                  {
                    label: "Set on every well",
                    onClick: () => {
                      const found = marker(markerMenu.id);
                      if (found)
                        void actOnMarker.applyToAll(
                          project.id,
                          found.name,
                          found.md,
                          wells.map((w) => w.id),
                        );
                    },
                  },
                  { separator: true, label: "" },
                  {
                    label: "Take off this well",
                    danger: true,
                    onClick: () => {
                      const found = marker(markerMenu.id);
                      if (found) void actOnMarker.remove(found.id, found.name, well.name);
                    },
                  },
                ]
          }
        />
      )}

      {markerDraft && (
        <MarkerDialog
          projectId={project.id}
          wellId={well.id}
          wellName={well.name}
          draft={markerDraft}
          onClose={() => setMarkerDraft(null)}
          onSaved={(message) => {
            toast(message);
            setMarkerDraft(null);
            onDataChanged();
          }}
        />
      )}
    </>
  );
}

function cellStyle(align: "left" | "right" = "right"): React.CSSProperties {
  return {
    padding: "6px 8px",
    textAlign: align,
    font: "400 10.5px var(--font-mono)",
    color: "var(--ink-2)",
    borderBottom: "1px solid var(--line-soft)",
  };
}

function ProjectTree({
  project,
  well,
  curveCount,
  zones,
  activeZone,
  onZoneSelect,
  onCurveInspect,
  onCollapse,
  onDataChanged,
  depthRange,
}: {
  project: Project;
  well: Well;
  curveCount: number;
  zones: Well["zones"];
  activeZone: string | null;
  onZoneSelect: (name: string) => void;
  /** Dipanggil sesudah sesuatu dihapus: sumur, setnya dan zonanya dimuat ulang. */
  onDataChanged: () => void;
  /** Selang kurva sumur ini, dipakai dialog ekspor sebagai bawaan kotaknya. */
  depthRange: [number, number] | null;
  /** Melipat rel ini menjadi pitanya; tombolnya duduk di kepala rel. */
  onCollapse: () => void;
  onCurveInspect: (curve: {
    id: number;
    mnemonic: string;
    color: string;
    versions: { id: number; label: string }[];
  }) => void;
}) {
  const toast = useToast();
  const mayWrite = canWrite(project.my_role);
  /**
   * Benda yang sedang ditanyakan penghapusannya.
   *
   * Satu keadaan untuk keempat jenisnya — kurva, set kurva, sumur, zona —
   * sebab yang berbeda di antara keempatnya hanyalah alamat dan kalimatnya,
   * dan empat dialog yang ditulis terpisah adalah empat tempat yang akan
   * berselisih tentang apa yang dikatakan sebelum sesuatu hilang.
   */
  const [doomed, setDoomed] = useState<{
    title: string;
    what: string;
    consequences: string[];
    confirmWord?: string;
    path: string;
    done: string;
  } | null>(null);
  const [deleting, setDeleting] = useState(false);
  /**
   * Kurva yang sedang ditawarkan untuk diekspor.
   *
   * Dibuka dari tiga tempat di pohon ini — sebuah kurva, sebuah set, atau
   * sumurnya — dan ketiganya membuka dialog yang sama dengan daftar yang
   * berbeda. Yang dipilih di baris pohon hanyalah *awal* pilihannya: dialognya
   * tetap memuat seluruh kurva sumur ini, sebab "ekspor GR" hampir selalu
   * berarti "GR dan beberapa lainnya".
   */
  const [exporting, setExporting] = useState<{ picked: number[] } | null>(null);

  const [query, setQuery] = useState("");
  // Only active sets open by default — every compute publishes a new version,
  // so expanding all of them would bury the current curves in history.
  const [expanded, setExpanded] = useState<Set<number>>(
    () => new Set(well.curve_sets.filter((s) => s.is_active).map((s) => s.id)),
  );

  const curves = useAsync(
    (signal) =>
      api
        .get<Page<{
          id: number; mnemonic: string; unit: string; curve_set: number;
          color: string; is_active?: boolean; off_reason?: string;
        }>>(
          "/api/curves/",
          { curve_set: well.curve_sets.map((s) => s.id).join(",") },
          signal,
        )
        .then((page) => unpage(page, signal)),
    [well.id, curveCount],
  );

  /**
   * Menyalakan atau mematikan satu log, lalu memuat ulang yang membacanya.
   *
   * Keduanya dimuat ulang, dan itu bukan kehati-hatian berlebih: daftar kurva
   * pohon ini dimuatnya sendiri — di situlah sakelarnya berubah warna —
   * sedangkan lembar log, `curve_sets`, dan tiap pemilih kurva di layar ini
   * datang dari muatan di atasnya. Memuat satu saja meninggalkan sakelar yang
   * sudah bergeser di sebelah kurva yang masih tergambar, dan yang terlihat
   * adalah tombol yang tidak bekerja.
   */
  const switchCurve = async (curve: { id: number; mnemonic: string },
                             on: boolean) => {
    try {
      await api.post("/api/curves/set-active/", { ids: [curve.id], is_active: on });
      toast(on
        ? `${curve.mnemonic} is used again on ${well.name}.`
        : `${curve.mnemonic} is no longer used on ${well.name} — nothing is computed or drawn from it.`);
      curves.reload();
      onDataChanged();
    } catch (cause) {
      toast(cause instanceof Error ? cause.message : String(cause), "error");
    }
  };

  const hapus = async () => {
    if (!doomed) return;
    setDeleting(true);
    try {
      await api.delete(doomed.path);
      toast(doomed.done);
      setDoomed(null);
      // Keduanya: daftar kurva pohon ini dimuatnya sendiri, sedangkan set,
      // zona dan sumurnya datang dari layar di atasnya.
      curves.reload();
      onDataChanged();
    } catch (cause) {
      toast(cause instanceof Error ? cause.message : String(cause), "error");
    } finally {
      setDeleting(false);
    }
  };

  const toggle = (id: number) =>
    setExpanded((current) => {
      const next = new Set(current);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const matches = (text: string) => !query || text.toLowerCase().includes(query.toLowerCase());

  return (
    <>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          // Tombol lipatnya setinggi 22 piksel; paddingnya menyusut supaya
          // kepala rel ini tidak tumbuh melebihi kepala rel di seberangnya.
          padding: "4px 7px 4px 11px",
          borderBottom: "1px solid var(--line-soft)",
          flex: "none",
        }}
      >
        <span className="label">Project</span>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ font: "400 9.5px var(--font-mono)", color: "var(--ink-6)" }}>
            {project.well_count} wells
          </span>
          {/* Di kepala rel, bukan di pemisah yang bisa diseret — pemisah itu
              sudah memikul pengubahan lebar. */}
          <RailCollapseButton
            side="left"
            collapsed={false}
            label="Hide the project tree"
            onClick={onCollapse}
          />
        </div>
      </div>

      <div style={{ margin: "9px 11px", flex: "none" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 7,
            height: 26,
            padding: "0 8px",
            background: "var(--control)",
            border: "1px solid var(--border-control)",
            borderRadius: "var(--radius)",
          }}
        >
          {Icon.search({ size: 11, color: "var(--ink-6)" })}
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search curves, sets, zones…"
            style={{
              flex: 1,
              minWidth: 0,
              background: "transparent",
              border: "none",
              outline: "none",
              font: "400 11px var(--font-sans)",
              color: "var(--ink)",
            }}
          />
        </div>
      </div>

      <div className="scroll-y" style={{ flex: 1, padding: "2px 0" }}>
        <TreeRow indent={2} bold label={project.name} meta={`${project.well_count} wells`} caret />
        <TreeRow
          indent={12}
          label={well.name}
          meta="active"
          caret
          accent
          exportLabel={`Export the curves of ${well.name} to LAS or CSV`}
          onExport={() =>
            setExporting({
              // Set yang aktif lebih dulu: itulah kurva yang sedang dipakai
              // orang, dan yang lama ada di dialognya kalau memang dicari.
              picked: (curves.data ?? [])
                .filter((curve) =>
                  curve.is_active !== false &&
                  well.curve_sets.find((set) => set.id === curve.curve_set)?.is_active)
                .map((curve) => curve.id),
            })
          }
          deleteLabel={`Delete well ${well.name} with everything in it`}
          onDelete={
            mayWrite
              ? () =>
                  setDoomed({
                    title: "Delete well",
                    what: `Well ${well.name}`,
                    // Yang ikut disebut satu per satu, dengan jumlahnya. Sebuah
                    // sumur bukan sebuah baris di pohon; ia adalah seluruh
                    // pekerjaan yang pernah dilakukan di atasnya.
                    consequences: [
                      `${well.curve_sets.length} curve sets`,
                      `${well.curve_sets.reduce((n, set) => n + set.curve_count, 0)} curves`,
                      `${zones.length} zones`,
                      "every marker and computation result it carries",
                    ],
                    // Nama sumurnya diketik ulang: satu tekanan tombol adalah
                    // harga yang terlalu murah untuk semua itu.
                    confirmWord: well.name,
                    path: `/api/wells/${well.id}/`,
                    done: `Well ${well.name} deleted.`,
                  })
              : undefined
          }
        />
        {well.curve_sets.map((set) => (
          <div key={set.id}>
            <TreeRow
              indent={26}
              label={set.name}
              // Yang dimatikan disebut di kepala setnya, dan itu yang membuat
              // sakelarnya bisa ditemukan tanpa membuka tiap set lebih dulu:
              // "12 · 3 off" pada set yang tertutup adalah satu-satunya tanda
              // di permukaan pohon bahwa ada log yang sengaja tidak dipakai di
              // dalamnya.
              meta={`v${set.version}${set.is_active ? "" : " · older"} · ${set.curve_count}`
                    + (set.off_count ? ` · ${set.off_count} off` : "")}
              caret
              accent={set.is_active}
              expanded={expanded.has(set.id)}
              onClick={() => toggle(set.id)}
              exportLabel={`Export ${set.name} v${set.version} to LAS or CSV`}
              onExport={() =>
                setExporting({
                  picked: (curves.data ?? [])
                    .filter((curve) =>
                      curve.curve_set === set.id && curve.is_active !== false)
                    .map((curve) => curve.id),
                })
              }
              deleteLabel={`Delete set ${set.name} v${set.version} with its curves`}
              onDelete={
                mayWrite
                  ? () =>
                      setDoomed({
                        title: "Delete curve set",
                        what: `${set.name} v${set.version}`,
                        consequences: [`the ${set.curve_count} curves inside it`],
                        path: `/api/curve-sets/${set.id}/`,
                        done: `${set.name} v${set.version} deleted along with its ${set.curve_count} curves.`,
                      })
                  : undefined
              }
            />
            {expanded.has(set.id) &&
              (curves.data ?? [])
                .filter((curve) => curve.curve_set === set.id && matches(curve.mnemonic))
                .map((curve) => (
                  <TreeRow
                    key={curve.id}
                    indent={40}
                    label={curve.mnemonic}
                    // Sebabnya yang dipajang ketika ada, dan satuannya ketika
                    // tidak: bagi log yang mati, "mengapa" adalah pertanyaan
                    // yang lebih dulu ditanyakan orang daripada "dalam satuan
                    // apa", dan jawabannya sudah tersimpan di sebelahnya.
                    meta={curve.is_active === false
                      ? curve.off_reason || "off"
                      : curve.unit}
                    dot={defaultCurveColor(curve.mnemonic, curve.color)}
                    mono
                    off={curve.is_active === false}
                    onSwitch={
                      mayWrite
                        ? () => switchCurve(curve, curve.is_active === false)
                        : undefined
                    }
                    switchLabel={
                      curve.is_active === false
                        ? `Use ${curve.mnemonic} again in every calculation and display`
                        : `Stop using ${curve.mnemonic} — it stays stored, but nothing is computed or drawn from it`
                    }
                    title={`Look inside ${curve.mnemonic}`}
                    exportLabel={`Export ${curve.mnemonic} to LAS or CSV`}
                    // Log yang mati tidak menawarkan ekspornya. Dialognya sudah
                    // tidak memuatnya, jadi tombol itu akan membuka daftar yang
                    // tidak mencentang apa pun — sebuah tombol yang terbaca
                    // seperti rusak, atas keadaan yang sebenarnya disengaja.
                    onExport={curve.is_active === false
                      ? undefined
                      : () => setExporting({ picked: [curve.id] })}
                    deleteLabel={`Delete curve ${curve.mnemonic} from ${set.name} v${set.version}`}
                    onDelete={
                      mayWrite
                        ? () =>
                            setDoomed({
                              title: "Delete curve",
                              what: `${curve.mnemonic} — ${set.name} v${set.version}`,
                              // Kurva bernama sama di set lain tidak ikut, dan
                              // itu perlu dikatakan: pohon ini mencetak nama
                              // yang sama sekali lagi di setiap versi set.
                              consequences: [],
                              path: `/api/curves/${curve.id}/`,
                              done: `${curve.mnemonic} deleted from ${set.name} v${set.version}.`,
                            })
                        : undefined
                    }
                    onClick={() =>
                      onCurveInspect({
                        id: curve.id,
                        mnemonic: curve.mnemonic,
                        color: defaultCurveColor(curve.mnemonic, curve.color),
                        // Kurva sejenis: mnemonic yang sama dari set lain,
                        // lalu kerabat sekeluarganya — PHIE terhadap PHIT,
                        // VSH terhadap VCL. Dikumpulkan di sini karena pohon
                        // ini sudah memegang keduanya — daftar kurva seluruh
                        // set dan nama beserta versi setnya — dan jendelanya
                        // tidak perlu memintanya lagi.
                        versions: kinOptions(
                          curve.mnemonic,
                          curve.id,
                          (curves.data ?? []).map((other) => {
                            const from = well.curve_sets.find((set) => set.id === other.curve_set);
                            return {
                              id: other.id,
                              mnemonic: other.mnemonic,
                              setLabel: from
                                ? `${from.name} v${from.version}${from.is_active ? "" : " · older"}`
                                : `set ${other.curve_set}`,
                              active: from?.is_active,
                              off: other.is_active === false,
                            };
                          }),
                        ),
                      })
                    }
                  />
                ))}
          </div>
        ))}
      </div>

      <div
        style={{
          borderTop: "1px solid var(--line-soft)",
          padding: "9px 11px",
          display: "flex",
          flexDirection: "column",
          gap: 7,
          flex: "none",
        }}
      >
        <span className="label">Active zone</span>
        {zones.length === 0 && (
          <span style={{ font: "400 10.5px var(--font-sans)", color: "var(--ink-6)" }}>No zones yet.</span>
        )}
        {zones.map((zone) => {
          const active = zone.name === activeZone;
          return (
            <button
              key={zone.id}
              type="button"
              onClick={() => onZoneSelect(zone.name)}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 8,
                padding: "6px 8px",
                background: active ? "var(--accent-wash)" : "transparent",
                border: `1px solid ${active ? "var(--accent-zone-line)" : "var(--border-control)"}`,
                borderLeft: `2px solid ${active ? "var(--accent)" : "transparent"}`,
                borderRadius: "var(--radius)",
                cursor: "pointer",
                textAlign: "left",
              }}
            >
              <span style={{ font: "500 11px var(--font-sans)", color: active ? "var(--ink)" : "var(--ink-3)" }}>
                {zone.name}
              </span>
              <span style={{ font: "400 10px var(--font-mono)", color: active ? "var(--accent)" : "var(--ink-6)" }}>
                {zone.top_md.toFixed(0)}–{zone.base_md.toFixed(0)} m
              </span>
              {/* Di dalam kartunya, dan bukan di baris tersendiri: kartu zona
                  ini bukan `TreeRow`, dan sebuah tong sampah yang berdiri di
                  luar kartunya akan menunjuk ke ruang kosong di antara dua
                  kartu. Tetap sebuah <span>, sebab kartunya sendiri sudah
                  sebuah <button>. */}
              {mayWrite && (
                <span
                  role="button"
                  tabIndex={0}
                  title={`Delete zone ${zone.name}`}
                  aria-label={`Delete zone ${zone.name}`}
                  onClick={(event) => {
                    event.stopPropagation();
                    setDoomed({
                      title: "Delete zone",
                      what: `Zone ${zone.name}`,
                      consequences: [],
                      path: `/api/zones/${zone.id}/`,
                      done: `Zone ${zone.name} deleted.`,
                    });
                  }}
                  onKeyDown={(event) => {
                    if (event.key !== "Enter" && event.key !== " ") return;
                    event.preventDefault();
                    event.stopPropagation();
                    setDoomed({
                      title: "Delete zone",
                      what: `Zone ${zone.name}`,
                      consequences: [],
                      path: `/api/zones/${zone.id}/`,
                      done: `Zone ${zone.name} deleted.`,
                    });
                  }}
                  style={{ display: "flex", alignItems: "center", color: "var(--ink-6)", cursor: "pointer" }}
                >
                  {Icon.trash({ size: 11 })}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {exporting && (
        <ExportDialog
          wellName={well.name}
          wellId={well.id}
          depthRange={depthRange}
          // Log yang dimatikan tidak ditawarkan sama sekali, dan tidak cukup
          // dipajang redup. Sebuah berkas LAS meninggalkan aplikasi ini dan
          // dibaca di tempat yang tidak punya sakelarnya: yang menerimanya tidak
          // bisa tahu bahwa densitas di dalamnya justru kurva yang di sini
          // sengaja tidak dipakai. Server menolaknya juga — lihat
          // `exports/logdata.gather` — dan yang dicegah di sini adalah orang
          // mencentang delapan kurva untuk kemudian diberi tahu bahwa satu di
          // antaranya membatalkan seluruh ekspornya.
          curves={(curves.data ?? [])
            .filter((curve) => curve.is_active !== false)
            .map((curve): ExportCurve => {
            const from = well.curve_sets.find((set) => set.id === curve.curve_set);
            return {
              id: curve.id,
              mnemonic: curve.mnemonic,
              unit: curve.unit,
              setLabel: from
                ? `${from.name} v${from.version}${from.is_active ? "" : " · older"}`
                : `set ${curve.curve_set}`,
              active: Boolean(from?.is_active),
            };
          })}
          initial={exporting.picked}
          onClose={() => setExporting(null)}
          onDone={(message) => toast(message)}
        />
      )}

      {doomed && (
        <DeleteDialog
          title={doomed.title}
          what={doomed.what}
          consequences={doomed.consequences}
          confirmWord={doomed.confirmWord}
          busy={deleting}
          onCancel={() => setDoomed(null)}
          onConfirm={hapus}
        />
      )}
    </>
  );
}

function TreeRow({
  indent,
  label,
  meta,
  caret,
  dot,
  bold,
  accent,
  mono,
  expanded,
  onClick,
  title,
  onDelete,
  deleteLabel,
  onExport,
  exportLabel,
  onSwitch,
  switchLabel,
  off,
}: {
  indent: number;
  label: string;
  meta?: string;
  caret?: boolean;
  dot?: string;
  bold?: boolean;
  accent?: boolean;
  mono?: boolean;
  expanded?: boolean;
  onClick?: () => void;
  title?: string;
  /**
   * Menghapus benda yang diwakili baris ini. Kosong berarti baris yang tidak
   * bisa dihapus — dan itu bukan hanya soal peran: proyek tidak dihapus dari
   * pohon sebuah sumur.
   */
  onDelete?: () => void;
  deleteLabel?: string;
  /** Membuka dialog ekspor dengan baris ini sebagai pilihan awalnya. */
  onExport?: () => void;
  exportLabel?: string;
  /**
   * Menyalakan atau mematikan log baris ini. Kosong berarti baris yang bukan
   * sebuah log — sumur, set, zona — atau pembaca yang tidak boleh menggesernya.
   */
  onSwitch?: () => void;
  switchLabel?: string;
  /**
   * Barisnya mewakili log yang sedang mati.
   *
   * Dipajang redup **dan** dengan sakelarnya yang tidak ikut bersembunyi saat
   * tetikus pergi. Keduanya perlu: yang redup saja terbaca seperti baris yang
   * belum selesai dimuat, dan sakelar yang hanya muncul saat disentuh membuat
   * satu-satunya jalan menyalakan kembali sebuah log bergantung pada
   * menemukannya lebih dulu tanpa satu pun tanda bahwa ia ada.
   */
  off?: boolean;
}) {
  // Tombol hapusnya muncul ketika barisnya disentuh atau difokus, dan tidak
  // sebelum itu: sebuah tong sampah yang berdiri di setiap baris sepanjang
  // enam puluh baris pohon adalah enam puluh ajakan menghapus sesuatu.
  const [hover, setHover] = useState(false);
  return (
    <div
      style={{ display: "flex", alignItems: "center" }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onFocus={() => setHover(true)}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node)) setHover(false);
      }}
    >
    <button
      type="button"
      onClick={onClick}
      title={title}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 7,
        width: "100%",
        height: 23,
        padding: `0 10px 0 ${indent}px`,
        border: "none",
        background: "transparent",
        cursor: onClick ? "pointer" : "default",
        textAlign: "left",
      }}
      onMouseEnter={(event) => (event.currentTarget.style.background = "var(--hover-soft)")}
      onMouseLeave={(event) => (event.currentTarget.style.background = "transparent")}
    >
      {caret && (
        <svg
          width="7"
          height="7"
          viewBox="0 0 7 7"
          style={{ flex: "none", transform: expanded === false ? "rotate(-90deg)" : undefined }}
          aria-hidden
        >
          <path d="M0 1 L7 1 L3.5 6 Z" fill="var(--ink-6)" />
        </svg>
      )}
      {dot && (
        <span style={{
          width: 6, height: 6, borderRadius: 2, flex: "none",
          // Titik warnanya menjadi cangkang kosong ketika lognya mati. Sebuah
          // titik pejal di sebelah nama yang redup tetap berkata "kurva ini
          // tergambar dengan warna ini", dan ia tidak tergambar di mana pun.
          background: off ? "transparent" : dot,
          boxShadow: off ? `inset 0 0 0 1px var(--ink-7)` : undefined,
        }} />
      )}
      <span
        style={{
          font: `${bold ? 600 : 500} 11px ${mono ? "var(--font-mono)" : "var(--font-sans)"}`,
          color: off ? "var(--ink-7)" : accent || bold ? "var(--ink)" : "var(--ink-3)",
          textDecoration: off ? "line-through" : undefined,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {label}
      </span>
      <span style={{ flex: 1 }} />
      {meta && (
        <span style={{
          font: "400 9.5px var(--font-mono)",
          color: "var(--ink-7)",
          whiteSpace: "nowrap",
        }}>
          {meta}
        </span>
      )}
    </button>
    {onSwitch && (
      <button
        type="button"
        title={switchLabel ?? (off ? `Use ${label} again` : `Stop using ${label}`)}
        aria-label={switchLabel ?? (off ? `Use ${label} again` : `Stop using ${label}`)}
        aria-pressed={off ? true : false}
        onClick={onSwitch}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: 20,
          height: 23,
          flex: "none",
          padding: 0,
          border: "none",
          background: "transparent",
          // Yang mati tidak ikut bersembunyi. Sebuah log yang dimatikan harus
          // bisa dinyalakan kembali oleh orang yang tidak tahu bahwa ia mati,
          // dan sakelar yang hanya muncul saat disentuh menuntut ia tahu lebih
          // dulu. Yang menyala tetap seperti tetangganya — sebuah sakelar di
          // tiap baris sepanjang enam puluh baris adalah enam puluh ajakan.
          visibility: off || hover ? "visible" : "hidden",
          color: off ? "var(--amber)" : "var(--ink-6)",
          cursor: "pointer",
        }}
        onMouseEnter={(event) => (event.currentTarget.style.color = "var(--accent)")}
        onMouseLeave={(event) =>
          (event.currentTarget.style.color = off ? "var(--amber)" : "var(--ink-6)")}
      >
        {Icon.power({ size: 11 })}
      </button>
    )}
    {onExport && (
      <button
        type="button"
        title={exportLabel ?? `Export ${label}`}
        aria-label={exportLabel ?? `Export ${label}`}
        onClick={onExport}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: 20,
          height: 23,
          flex: "none",
          padding: 0,
          border: "none",
          background: "transparent",
          visibility: hover ? "visible" : "hidden",
          color: "var(--ink-6)",
          cursor: "pointer",
        }}
        onMouseEnter={(event) => (event.currentTarget.style.color = "var(--accent)")}
        onMouseLeave={(event) => (event.currentTarget.style.color = "var(--ink-6)")}
      >
        {/* Panah ke bawah pada garis: berkas yang keluar dari aplikasi ini,
            bukan LAS yang masuk — ikon impor sudah dipakai di bilah atas. */}
        <svg width="11" height="11" viewBox="0 0 16 16" aria-hidden>
          <path d="M8 2.5 L8 10 M4.5 7 L8 10.5 L11.5 7 M3 13 L13 13"
                stroke="currentColor" strokeWidth="1.3" fill="none"
                strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
    )}
    {onDelete && (
      <button
        type="button"
        title={deleteLabel ?? `Delete ${label}`}
        aria-label={deleteLabel ?? `Delete ${label}`}
        onClick={onDelete}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: 20,
          height: 23,
          flex: "none",
          padding: 0,
          border: "none",
          background: "transparent",
          // Tak terlihat, tetapi tetap menempati tempatnya: tombol yang lahir
          // ketika tetikus tiba menggeser meta baris itu satu tarikan napas
          // sebelum dibaca.
          visibility: hover ? "visible" : "hidden",
          color: "var(--ink-6)",
          cursor: "pointer",
        }}
        onMouseEnter={(event) => (event.currentTarget.style.color = "var(--rose)")}
        onMouseLeave={(event) => (event.currentTarget.style.color = "var(--ink-6)")}
      >
        {Icon.trash({ size: 11 })}
      </button>
    )}
    </div>
  );
}
